import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Shared navigation header for every public/marketing surface:
 * LandingPage, PricingPage, FeaturesPage, LoginPage, SignupPage.
 *
 * Behavior:
 *  - Sticky, transparent at top → frosted glass after scroll.
 *  - Active route gets the gold underline + white text so the user
 *    always knows where they are.
 *  - The nav-link list NEVER changes between routes — the "Sign In"
 *    link is intentionally removed from the primary nav (we already
 *    have a dedicated "Sign In" route-action via the right-rail CTA),
 *    which used to be the source of jitter when navigating to /login.
 *  - The right-rail CTA always renders something — even on auth pages —
 *    so the flex children don't collapse and the nav links stay locked
 *    in place between page transitions.
 */
export default function MarketingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isLogin  = location.pathname === '/login';
  const isSignup = location.pathname === '/signup';

  // Primary nav is identical on every public page — no conditional
  // entries that would reflow the layout between routes. When the user
  // is logged in, the "Sign In" item is replaced by nothing (their
  // right-rail "Open app" CTA covers it).
  const links = [
    { kind: 'route', label: 'Home',     to: '/',         testId: 'nav-home'     },
    { kind: 'route', label: 'Features', to: '/features', testId: 'nav-features' },
    { kind: 'route', label: 'Pricing',  to: '/pricing',  testId: 'nav-pricing'  },
  ];
  if (!user) links.push({ kind: 'route', label: 'Sign In', to: '/login', testId: 'nav-signin' });

  const isActive = (link) => {
    if (link.to === '/') return location.pathname === '/';
    return location.pathname === link.to;
  };

  /**
   * Decide what to render in the right-rail CTA slot.
   * Critical: this slot is ALWAYS visible (never returns null) so the
   * flex layout has a consistent right anchor and the nav links don't
   * shift sideways between routes.
   */
  const renderCta = (mobile = false) => {
    const baseDesktop = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-bold transition-all';
    const baseMobile  = 'w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg font-bold';
    const base = mobile ? baseMobile : baseDesktop;
    const primary   = 'bg-[#CBA33B] hover:bg-[#D8B257] text-[#0D1520] hover:shadow-[0_8px_28px_rgba(203,163,59,0.35)]';
    const secondary = 'border border-white/[0.12] bg-white/[0.04] hover:bg-white/[0.08] text-white';

    if (user) {
      return (
        <button
          onClick={() => { setMobileOpen(false); navigate('/app'); }}
          data-testid="header-go-app"
          className={`${base} ${primary}`}
        >
          Open app <ArrowRight size={14} strokeWidth={2.8} />
        </button>
      );
    }
    if (isLogin) {
      return (
        <button
          onClick={() => { setMobileOpen(false); navigate('/signup'); }}
          data-testid="header-cta-signup"
          className={`${base} ${primary}`}
        >
          Create account <ArrowRight size={14} strokeWidth={2.8} />
        </button>
      );
    }
    if (isSignup) {
      return (
        <button
          onClick={() => { setMobileOpen(false); navigate('/login'); }}
          data-testid="header-cta-signin"
          className={`${base} ${secondary}`}
        >
          Sign in <ArrowRight size={14} strokeWidth={2.8} />
        </button>
      );
    }
    return (
      <button
        onClick={() => { setMobileOpen(false); navigate('/free-trial'); }}
        data-testid="header-cta"
        className={`${base} ${primary}`}
      >
        Start Free Trial <ArrowRight size={14} strokeWidth={2.8} />
      </button>
    );
  };

  return (
    <header
      data-testid="marketing-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-[#0D1520]/90 backdrop-blur-xl border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
      // Push down by iOS status bar height on notched phones so the
      // logo/nav don't render behind the phone's clock. Zero on
      // Android/desktop where the inset is 0.
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-brand" className="flex items-center gap-2.5 group" onClick={() => setMobileOpen(false)}>
          <img src="/mantle-logo.png" alt="" className="w-8 h-8 object-contain group-hover:drop-shadow-[0_0_18px_rgba(203,163,59,0.5)] transition-all" />
          <span className="text-[17px] font-bold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Mantle</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-[13.5px] font-medium" aria-label="Primary">
          {links.map(l => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={l.testId}
              className={`relative py-1 transition-colors ${isActive(l) ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              {l.label}
              {isActive(l) && <span className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-[#CBA33B]" />}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center justify-end gap-2 min-w-[180px]">
          {renderCta(false)}
        </div>

        <button
          onClick={() => setMobileOpen(o => !o)}
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 -mr-2 text-zinc-300"
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-white/[0.06] bg-[#0D1520]/95 backdrop-blur-xl">
          <div className="px-5 py-6 space-y-1 text-[15px] font-medium">
            {links.map(l => (
              <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)}
                className={`block py-2.5 px-2 rounded-md ${isActive(l) ? 'text-white bg-white/[0.04]' : 'text-zinc-300 hover:text-white hover:bg-white/[0.03]'}`}
              >{l.label}</Link>
            ))}
            <div className="pt-3">
              {renderCta(true)}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

